import { useRouteError, isRouteErrorResponse, useNavigate } from "react-router";
import { AlertTriangle, Home, RefreshCw, Mail, ChevronDown } from "lucide-react";
import { Button } from "@/shared/components/ui/button";
import { useEffect } from "react";
import { EventType, trackEvent } from "@/shared/utils/core";
import { sentryUtils } from "@/shared/utils/sentry";
import { useGetEnvironment } from "@/shared/hooks/useGetEnvironment";

export function RouterErrorBoundary() {
  const error = useRouteError();
  const navigate = useNavigate();
  const environment = useGetEnvironment();
  const isDev = environment !== "prod";

  useEffect(() => {
    console.error("Router Error:", error);

    let errorMessage = "Unknown error";
    let errorStatus: number | undefined;

    if (isRouteErrorResponse(error)) {
      errorMessage = error.statusText || `Error ${error.status}`;
      errorStatus = error.status;
    } else if (error instanceof Error) {
      errorMessage = error.message;
    } else if (typeof error === "string") {
      errorMessage = error;
    }

    trackEvent(EventType.ERROR_ENCOUNTERED, {
      error_message: errorMessage,
      error_type: "router_error",
      error_status: errorStatus,
      error_stack: error instanceof Error ? error.stack?.substring(0, 500) : undefined,
    });

    if (error instanceof Error) {
      sentryUtils.captureError(error);
    } else if (isRouteErrorResponse(error)) {
      sentryUtils.logApiError(
        String(error.data?.url ?? "route"),
        error.status,
        error.statusText || `Route error ${error.status}`,
      );
    } else {
      sentryUtils.captureError(error, "Router error");
    }
  }, [error]);

  const handleReload = (): void => {
    window.location.reload();
  };

  const handleGoHome = (): void => {
    navigate("/");
  };

  let errorDetails: string | null = null;

  if (isRouteErrorResponse(error)) {
    errorDetails = error.statusText || `Error ${error.status}`;
  } else if (error instanceof Error) {
    errorDetails = error.message;
  } else if (typeof error === "string") {
    errorDetails = error;
  }

  return (
    <div className="min-h-screen bg-subtle flex items-center justify-center p-4">
      <div className="w-full max-w-3xl">
        <div className="bg-white rounded-2xl overflow-hidden">
          <div className="p-12 space-y-6">
            <div className="flex justify-center">
              <div className="bg-red-50 rounded-full p-4">
                <AlertTriangle className="w-10 h-10 text-red-500" strokeWidth={1.5} />
              </div>
            </div>

            <div className="text-center space-y-3">
              <h1>Something went wrong</h1>
              <p className="text-secondary">
                We encountered an unexpected error. Our team has been notified.
              </p>
            </div>

            {isDev && errorDetails && (
              <details className="group">
                <summary className="cursor-pointer text-sm font-medium text-secondary hover:text-primary flex items-center gap-2">
                  Click to show stack trace (Local/Dev Only)
                  <ChevronDown size={16} />
                </summary>

                <div className="mt-4 p-4 bg-subtle rounded-lg border border-primary overflow-auto max-h-[300px]">
                  <p className="text-sm font-mono text-red-600 mb-2">{errorDetails}</p>
                  {error instanceof Error && error.stack && (
                    <pre className="text-sm text-secondary overflow-x-auto whitespace-pre-wrap">
                      {error.stack}
                    </pre>
                  )}
                </div>
              </details>
            )}

            <div className="flex flex-col sm:flex-row gap-3 pt-4">
              <Button onClick={handleReload} variant="brand" className="flex-1 h-12">
                <RefreshCw className="w-4 h-4" />
                Reload Page
              </Button>
              <Button onClick={handleGoHome} variant="outline" className="flex-1 h-12">
                <Home className="w-4 h-4" />
                Go to Home
              </Button>
            </div>

            <div className="pt-6 border-t border-primary">
              <div className="flex items-start gap-3 text-sm text-secondary">
                <Mail className="w-5 h-5 text-tertiary flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium mb-1">Need help?</p>
                  <p>
                    If this problem persists, please contact our support team at{" "}
                    <a
                      href="mailto:support@elayne.com"
                      className="text-brand hover:underline font-medium"
                    >
                      support@elayne.com
                    </a>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="text-center mt-6 text-sm text-tertiary">
          <p>Error Code: {Date.now().toString(36).toUpperCase()}</p>
        </div>
      </div>
    </div>
  );
}
